/**
 * Lift2Fit — the lifter's own Programme.
 *
 * The signed-in, editable version of the landing page's "The Programme": the
 * days of the active block as cards in the same visual language, a builder that
 * creates a week from scratch with no AI, in-place editing of days and
 * prescriptions, and an honest history with activation.
 *
 * Every mutation goes through `lib/api.ts`, which follows the 0007 contract and
 * falls back to the built-in demo programme when a route is not deployed — the
 * `SourceNotice` then says so.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type ApiResult,
  type ExerciseGroupType,
  type ExerciseOption,
  type PlanHistoryEntry,
  type ProgrammeDayPatch,
  type ProgrammeDraft,
  type ProgrammeExerciseInput,
  type ProgrammeExercisePatch,
  type ProgrammeOverview,
  type ProgrammeRow,
} from '@/lib/api';
import { cn, formatDate, pluralize } from '@/lib/format';
import { RPE_REFERENCE, formatLoggedSet, formatPrescription, formatReps, type PrescriptionLine } from '@/lib/prescription';
import { displayToKg, formatWeight, kgToDisplay } from '@/lib/units';
import { useSession } from '@/lib/useSession';
import { useUnitPreference } from '@/lib/useUnitPreference';
import {
  ArrowRightIcon,
  BarbellIcon,
  CheckIcon,
  ChevronDownIcon,
  GridIcon,
  LockIcon,
  PlusIcon,
  SparkIcon,
  TimerIcon,
  TrashIcon,
} from '@/lib/icons';
import { SourceNotice } from '../blocks';
import { AllProgrammesPanel } from '../all-programmes';
import { NewBlockPanel, utcToday, type NewBlockChoice } from '../new-block';
import { ScheduledBlockBanner, CoachBlockReadyBanner } from '../scheduled-block';
import {
  CollapsedNote,
  EmptyState,
  Field,
  LiveMessage,
  LoadingPanel,
  LockedCoachSlot,
  Panel,
  PreviousSetLine,
  ScreenHeader,
  StatusChip,
  groupLetter,
  groupTagText,
  type ChipTone,
} from '../components';
import { MovementPicker } from '../pickers';
import { WarmupCard } from '../warmup';
import { buildBlocks, moveWithinCluster, nextGroupNumber } from '../grouping';
import { requestRuntimeOpen } from '../session-runtime';
import { unlockRuntimeAudio } from '../runtime-sounds';
import { Link, navigate } from '../router';
import { invalidate, isFresh, peek, write } from '@/lib/readCache';

const TEMPO_PATTERN = /^[0-9Xx]-[0-9Xx]-[0-9Xx]-[0-9Xx]$/;

/** Shared plain-English help for the programme fields (the ⓘ popovers). */
const FIELD_HELP = {
  sets: 'How many working sets to do. Warm-ups are not counted here — log them separately in the Grid.',
  repMin: 'The bottom of the rep range. You progress by adding reps until you reach the top, then adding load.',
  repMax: 'The top of the rep range. Hit this at the target RPE and the next step is a little more weight.',
  targetRpe: 'How hard the last rep should feel, 1–10. RPE 7 means about three reps left in the tank; leave it blank if you do not track effort.',
  targetWeight: 'The load you intend to use, in your unit. The Grid shows it in an empty set cell as the starting point — log what you actually lift.',
  rest: 'How long to wait before the next set; the timer alerts you when it is up.',
  tempo: 'The lifting speed as eccentric–pause–concentric–pause seconds, e.g. 3-1-1-0. Use X for an explosive phase.',
  group: 'Perform this movement back to back with the others in the same group. A superset alternates movements; a drop set keeps the same movement and lowers the load.',
  notes: 'A cue for this movement only you will see — setup, a niggle, a bar-speed reminder. Collapsed by default so the grid stays dense.',
} as const;

/* ---------------------------------------------------------------------------
   Prescription adapter — one vocabulary with the landing page and The Grid.
   ------------------------------------------------------------------------ */

function toLine(row: ProgrammeRow): PrescriptionLine {
  const distance = row.measure === 'distance_m' ? (row.repMax || row.repMin) : null;
  const duration = row.measure === 'time_s' ? (row.repMax || row.repMin) : null;
  return {
    supersetGroup: row.supersetGroup,
    prescribedSets: row.prescribedSets,
    repMin: row.measure === 'reps' || row.measure === 'amrap' ? row.repMin : null,
    repMax: row.measure === 'reps' || row.measure === 'amrap' ? row.repMax : null,
    measure: row.measure,
    perSide: row.perSide,
    isBodyweight: row.isBodyweight,
    tempo: row.tempo,
    targetRpe: row.targetRpe,
    percent1rm: row.percent1rm,
    targetWeightKg: row.targetWeightKg,
    targetDistanceM: distance,
    targetDurationS: duration,
    restSeconds: row.restSeconds,
  };
}

interface ProgrammeDay {
  dayId: string;
  dayIndex: number;
  label: string;
  focus: string[];
  rows: ProgrammeRow[];
}

/**
 * Prefer the API's `days` envelope when it is present, so a day with no
 * prescribed exercise still renders and can receive its first movement; fall
 * back to grouping `rows` on API builds that only send prescriptions.
 */
function buildDays(overview: ProgrammeOverview): ProgrammeDay[] {
  const byDay = new Map<string, ProgrammeDay>();
  const ensure = (dayId: string, dayIndex: number, label: string, focus: string[]): ProgrammeDay => {
    const existing = byDay.get(dayId);
    if (existing) return existing;
    const day: ProgrammeDay = { dayId, dayIndex, label, focus, rows: [] };
    byDay.set(dayId, day);
    return day;
  };
  for (const day of overview.days ?? []) ensure(day.id, day.dayIndex, day.label, day.focus);
  for (const row of overview.rows) ensure(row.dayId, row.dayIndex, row.dayLabel, row.dayFocus).rows.push(row);
  for (const day of byDay.values()) day.rows.sort((a, b) => a.orderIndex - b.orderIndex);
  return [...byDay.values()].sort((a, b) => a.dayIndex - b.dayIndex);
}

/* ---------------------------------------------------------------------------
   The ⓘ-labelled field helpers used by both exercise forms.
   ------------------------------------------------------------------------ */

function NumberField({
  label,
  value,
  onChange,
  info,
  min,
  max,
  step,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  info: string;
  min?: number;
  max?: number;
  step?: number;
  testId?: string;
}) {
  return (
    <Field label={label} info={info} testId={testId}>
      <input
        type="number"
        inputMode="numeric"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        className="l2f-tabular h-10 w-full border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
      />
    </Field>
  );
}

function GroupSelect({
  value,
  onChange,
  label = 'Group',
  testId,
}: {
  value: { group: number | null; groupType: ExerciseGroupType | null };
  onChange: (value: { group: number | null; groupType: ExerciseGroupType | null }) => void;
  label?: string;
  testId?: string;
}) {
  const encoded = value.group === null ? 'none' : `${value.groupType ?? 'superset'}:${value.group}`;
  return (
    <Field label={label} info={FIELD_HELP.group} testId={testId ?? 'group'}>
      <select
        aria-label={label}
        value={encoded}
        data-testid={testId ?? 'group'}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === 'none') {
            onChange({ group: null, groupType: null });
            return;
          }
          const [type, number] = raw.split(':');
          onChange({
            group: Number.parseInt(number ?? '1', 10),
            groupType: type === 'drop_set' ? 'drop_set' : 'superset',
          });
        }}
        className="h-10 w-full border border-hairline bg-page px-2 text-[13px] text-ink"
      >
        <option value="none">No group</option>
        {Array.from({ length: 6 }, (_, index) => index + 1).map((group) => (
          <optgroup key={group} label={`Cluster ${groupLetter(group)}`}>
            <option value={`superset:${group}`}>{`Superset ${groupLetter(group)}`}</option>
            <option value={`drop_set:${group}`}>{`Drop set ${groupLetter(group)}`}</option>
          </optgroup>
        ))}
      </select>
    </Field>
  );
}

function TempoField({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  testId?: string;
}) {
  const invalid = value.trim() !== '' && !TEMPO_PATTERN.test(value.trim());
  return (
    <Field label="Tempo" info={FIELD_HELP.tempo} testId={testId ?? 'tempo'}>
      <input
        type="text"
        inputMode="text"
        aria-label="Tempo"
        placeholder="3-1-1-0"
        value={value}
        data-testid={testId ?? 'tempo'}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={invalid}
        className={cn(
          'l2f-tabular h-10 w-full border bg-page px-2 font-mono text-[13px] text-ink',
          invalid ? 'border-danger text-danger' : 'border-hairline',
        )}
      />
      {invalid ? (
        <span className="text-[10px] text-danger">Use four phases, e.g. 3-1-1-0 (X = explosive).</span>
      ) : null}
    </Field>
  );
}

function NotesField({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  testId?: string;
}) {
  return (
    <Field label="Notes" info={FIELD_HELP.notes} testId={testId ?? 'notes'} className="sm:col-span-2 lg:col-span-3">
      <textarea
        rows={2}
        maxLength={500}
        aria-label="Notes"
        placeholder="Cues, niggles, bar speed…"
        value={value}
        data-testid={testId ?? 'notes'}
        onChange={(event) => onChange(event.target.value)}
        className="w-full resize-none border border-hairline bg-page px-2 py-1.5 text-[13px] leading-snug text-ink placeholder:text-ink-muted"
      />
    </Field>
  );
}

/* ---------------------------------------------------------------------------
   The exercise editor
   ------------------------------------------------------------------------ */

interface ExerciseDraft {
  sets: string;
  repMin: string;
  repMax: string;
  targetRpe: string;
  targetWeightKg: string;
  restSeconds: string;
  tempo: string;
  group: { group: number | null; groupType: ExerciseGroupType | null };
  notes: string;
}

function clampInt(value: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

function draftFromRow(row: ProgrammeRow, unit: 'metric' | 'imperial'): ExerciseDraft {
  return {
    sets: String(row.prescribedSets),
    repMin: String(row.repMin),
    repMax: String(row.repMax),
    targetRpe: row.targetRpe === null ? '' : String(row.targetRpe),
    targetWeightKg: row.targetWeightKg === null ? '' : String(kgToDisplay(row.targetWeightKg, unit)),
    restSeconds: String(row.restSeconds),
    tempo: row.tempo ?? '',
    group: { group: row.supersetGroup, groupType: row.groupType },
    notes: row.notes ?? '',
  };
}

function blankDraft(): ExerciseDraft {
  return {
    sets: '3',
    repMin: '8',
    repMax: '12',
    targetRpe: '7',
    targetWeightKg: '',
    restSeconds: '90',
    tempo: '',
    group: { group: null, groupType: null },
    notes: '',
  };
}

/** Draft → wire patch, clamped to the ranges the API accepts. */
function draftToPatch(draft: ExerciseDraft, unit: 'metric' | 'imperial'): ProgrammeExercisePatch {
  const repMin = clampInt(draft.repMin, 1, 100, 8);
  const tempo = draft.tempo.trim();
  const rpe = draft.targetRpe.trim() === '' ? null : Number.parseFloat(draft.targetRpe);
  return {
    sets: clampInt(draft.sets, 1, 20, 3),
    repMin,
    repMax: Math.max(repMin, clampInt(draft.repMax, 1, 100, repMin)),
    targetRpe: rpe === null || Number.isNaN(rpe) ? null : Math.max(1, Math.min(10, rpe)),
    targetWeightKg:
      draft.targetWeightKg.trim() === '' ? null : displayToKg(Number.parseFloat(draft.targetWeightKg) || 0, unit),
    restSeconds: clampInt(draft.restSeconds, 0, 900, 90),
    tempo: tempo !== '' && TEMPO_PATTERN.test(tempo) ? tempo : null,
    supersetGroup: draft.group.group,
    groupType: draft.group.group === null ? null : (draft.group.groupType ?? 'superset'),
    notes: draft.notes.trim() === '' ? null : draft.notes.trim().slice(0, 500),
  };
}

/** The eight fields every add/edit exercise form carries, each with its ⓘ. */
function ExerciseFormFields({
  draft,
  onChange,
  unit,
  idPrefix,
}: {
  draft: ExerciseDraft;
  onChange: (draft: ExerciseDraft) => void;
  unit: 'metric' | 'imperial';
  idPrefix: string;
}) {
  const set = (patch: Partial<ExerciseDraft>) => onChange({ ...draft, ...patch });
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <NumberField
        label="Sets"
        info={FIELD_HELP.sets}
        value={draft.sets}
        onChange={(sets) => set({ sets })}
        min={1}
        max={20}
        testId={`${idPrefix}-sets`}
      />
      <NumberField
        label="Rep min"
        info={FIELD_HELP.repMin}
        value={draft.repMin}
        onChange={(repMin) => set({ repMin })}
        min={1}
        max={100}
        testId={`${idPrefix}-rep-min`}
      />
      <NumberField
        label="Rep max"
        info={FIELD_HELP.repMax}
        value={draft.repMax}
        onChange={(repMax) => set({ repMax })}
        min={1}
        max={100}
        testId={`${idPrefix}-rep-max`}
      />
      <NumberField
        label="Target RPE"
        info={FIELD_HELP.targetRpe}
        value={draft.targetRpe}
        onChange={(targetRpe) => set({ targetRpe })}
        min={1}
        max={10}
        step={0.5}
        testId={`${idPrefix}-rpe`}
      />
      <Field label={`Target weight (${unit === 'imperial' ? 'lb' : 'kg'})`} info={FIELD_HELP.targetWeight} testId={`${idPrefix}-weight`}>
        <input
          type="number"
          inputMode="decimal"
          aria-label={`Target weight (${unit === 'imperial' ? 'lb' : 'kg'})`}
          value={draft.targetWeightKg}
          data-testid={`${idPrefix}-weight`}
          onChange={(event) => set({ targetWeightKg: event.target.value })}
          className="l2f-tabular h-10 w-full border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
        />
      </Field>
      <NumberField
        label="Rest (s)"
        info={FIELD_HELP.rest}
        value={draft.restSeconds}
        onChange={(restSeconds) => set({ restSeconds })}
        min={0}
        max={900}
        step={15}
        testId={`${idPrefix}-rest`}
      />
      <TempoField value={draft.tempo} onChange={(tempo) => set({ tempo })} testId={`${idPrefix}-tempo`} />
      <GroupSelect value={draft.group} onChange={(group) => set({ group })} testId={`${idPrefix}-group`} />
      <NotesField value={draft.notes} onChange={(notes) => set({ notes })} testId={`${idPrefix}-notes`} />
    </div>
  );
}

function ExerciseEditor({
  row,
  unit,
  busy,
  onSave,
  onCancel,
}: {
  row: ProgrammeRow;
  unit: 'metric' | 'imperial';
  busy: boolean;
  onSave: (patch: ProgrammeExercisePatch) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ExerciseDraft>(() => draftFromRow(row, unit));

  return (
    <div className="flex flex-col gap-3 border border-accent/60 bg-page-subtle p-3" data-testid="exercise-editor">
      <ExerciseFormFields draft={draft} onChange={setDraft} unit={unit} idPrefix="exercise" />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          data-testid="exercise-save"
          onClick={() => onSave(draftToPatch(draft, unit))}
          className="inline-flex h-10 items-center gap-2 bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          <CheckIcon className="text-[12px]" /> Save prescription
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-10 items-center border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Add a movement to a day: choose it from the catalogue, then set every
 * prescription field — including tempo, group and notes — before it is saved.
 */
function ExerciseComposer({
  dayLabel,
  movement,
  unit,
  busy,
  onChooseMovement,
  onSave,
  onCancel,
}: {
  dayLabel: string;
  movement: ExerciseOption | null;
  unit: 'metric' | 'imperial';
  busy: boolean;
  onChooseMovement: () => void;
  onSave: (input: ProgrammeExerciseInput, name: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ExerciseDraft>(blankDraft);

  return (
    <div className="flex flex-col gap-3 border border-accent/60 bg-page-subtle p-3" data-testid="exercise-composer">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
          Add a movement to {dayLabel}
        </span>
        <button
          type="button"
          onClick={onChooseMovement}
          data-testid="composer-choose-movement"
          className="inline-flex h-9 items-center gap-2 border border-hairline bg-surface px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          <PlusIcon className="text-[11px]" />
          {movement ? 'Change movement' : 'Choose movement'}
        </button>
      </div>
      {movement ? (
        <span className="text-[14px] font-bold text-ink" data-testid="composer-movement-name">
          {movement.name}
        </span>
      ) : (
        <p className="text-[12px] text-ink-muted">Pick a movement from the catalogue, then set its prescription.</p>
      )}
      <ExerciseFormFields draft={draft} onChange={setDraft} unit={unit} idPrefix="composer" />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || movement === null}
          data-testid="composer-save"
          onClick={() => {
            if (!movement) return;
            const patch = draftToPatch(draft, unit);
            onSave(
              {
                exerciseId: movement.id,
                sets: patch.sets ?? 3,
                repMin: patch.repMin ?? 8,
                repMax: patch.repMax ?? 12,
                targetRpe: patch.targetRpe ?? null,
                targetWeightKg: patch.targetWeightKg ?? null,
                restSeconds: patch.restSeconds ?? 90,
                tempo: patch.tempo ?? null,
                supersetGroup: patch.supersetGroup ?? null,
                groupType: patch.groupType ?? null,
                notes: patch.notes ?? null,
              },
              movement.name,
            );
          }}
          className="inline-flex h-10 items-center gap-2 bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          <CheckIcon className="text-[12px]" /> Add to the day
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-10 items-center border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The builder
   ------------------------------------------------------------------------ */

interface BuilderExercise {
  key: string;
  exerciseId: string;
  name: string;
  sets: number;
  repMin: number;
  repMax: number;
  targetRpe: number;
  targetWeightKg: string;
  restSeconds: number;
  tempo: string;
  notes: string;
  group: { group: number | null; groupType: ExerciseGroupType | null };
}

interface BuilderDay {
  key: string;
  label: string;
  focus: string;
  exercises: BuilderExercise[];
}

let builderKey = 0;
function key(prefix: string): string {
  builderKey += 1;
  return `${prefix}-${builderKey}`;
}

function emptyDay(index: number): BuilderDay {
  return { key: key('day'), label: `Day ${index}`, focus: '', exercises: [] };
}

function ProgrammeBuilder({
  unit,
  defaultName,
  defaultDays,
  busy,
  onCancel,
  onCreate,
}: {
  unit: 'metric' | 'imperial';
  /** The name chosen in New Block; the builder never renames it behind the user. */
  defaultName?: string;
  defaultDays: number;
  busy: boolean;
  onCancel: () => void;
  onCreate: (draft: ProgrammeDraft) => void;
}) {
  const [name, setName] = useState(defaultName ?? 'My training week');
  const [weeks, setWeeks] = useState(4);
  const [deload, setDeload] = useState<number | null>(4);
  const [days, setDays] = useState<BuilderDay[]>(() =>
    Array.from({ length: Math.max(1, defaultDays) }, (_, index) => emptyDay(index + 1)),
  );
  const [pickerDay, setPickerDay] = useState<string | null>(null);
  const [validation, setValidation] = useState<string | null>(null);

  const mutateDay = (dayKey: string, patch: Partial<BuilderDay>) => {
    setDays((prev) => prev.map((day) => (day.key === dayKey ? { ...day, ...patch } : day)));
  };

  const mutateExercise = (dayKey: string, exerciseKey: string, patch: Partial<BuilderExercise>) => {
    setDays((prev) =>
      prev.map((day) =>
        day.key === dayKey
          ? { ...day, exercises: day.exercises.map((exercise) => (exercise.key === exerciseKey ? { ...exercise, ...patch } : exercise)) }
          : day,
      ),
    );
  };

  const submit = () => {
    if (!name.trim()) {
      setValidation('Give the block a name.');
      return;
    }
    if (weeks < 1 || weeks > 52) {
      setValidation('Weeks must be between 1 and 52.');
      return;
    }
    if (!days.length) {
      setValidation('Add at least one day.');
      return;
    }
    const emptyDayIndex = days.findIndex((day) => day.exercises.length === 0);
    if (emptyDayIndex >= 0) {
      setValidation(`${days[emptyDayIndex]?.label ?? `Day ${emptyDayIndex + 1}`} needs at least one movement.`);
      return;
    }
    setValidation(null);
    onCreate({
      name: name.trim(),
      weeks,
      deloadWeek: deload,
      days: days.map((day) => ({
        label: day.label.trim() || 'Training day',
        focus: day.focus
          .split(/[,·/]/)
          .map((tag) => tag.trim())
          .filter(Boolean)
          .slice(0, 6),
        exercises: day.exercises.map((exercise): ProgrammeExerciseInput => {
          const repMin = Math.max(1, Math.min(100, exercise.repMin));
          const tempo = exercise.tempo.trim();
          return {
            exerciseId: exercise.exerciseId,
            sets: Math.max(1, Math.min(20, exercise.sets)),
            repMin,
            repMax: Math.max(repMin, Math.min(100, exercise.repMax || repMin)),
            targetRpe: exercise.targetRpe >= 1 && exercise.targetRpe <= 10 ? exercise.targetRpe : null,
            targetWeightKg:
              exercise.targetWeightKg.trim() === ''
                ? null
                : displayToKg(Number.parseFloat(exercise.targetWeightKg) || 0, unit),
            restSeconds: Math.max(0, Math.min(900, exercise.restSeconds)),
            tempo: tempo !== '' && TEMPO_PATTERN.test(tempo) ? tempo : null,
            supersetGroup: exercise.group.group,
            groupType: exercise.group.group === null ? null : (exercise.group.groupType ?? 'superset'),
            notes: exercise.notes.trim() === '' ? null : exercise.notes.trim().slice(0, 500),
          };
        }),
      })),
    });
  };

  return (
    <Panel
      title="Build the block"
      subtitle="No AI — choose the days, the movements and the numbers. You can edit any of it later."
      actions={
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-10 items-center border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
        >
          Cancel
        </button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-4">
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Block name</span>
          <input
            type="text"
            value={name}
            data-testid="builder-name"
            onChange={(event) => setName(event.target.value)}
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Weeks</span>
          <input
            type="number"
            min={1}
            max={52}
            value={weeks}
            data-testid="builder-weeks"
            onChange={(event) => {
              const next = Math.max(1, Math.min(52, Number.parseInt(event.target.value, 10) || 1));
              setWeeks(next);
              setDeload((current) => (current !== null && current > next ? null : current));
            }}
            className="l2f-tabular h-11 border border-hairline bg-page px-3 font-mono text-[15px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Deload week</span>
          <select
            value={deload === null ? 'none' : String(deload)}
            data-testid="builder-deload"
            onChange={(event) => setDeload(event.target.value === 'none' ? null : Number.parseInt(event.target.value, 10))}
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          >
            <option value="none">No deload</option>
            {Array.from({ length: weeks }, (_, index) => index + 1).map((week) => (
              <option key={week} value={week}>
                Week {week}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-col gap-4">
        {days.map((day, dayIndex) => (
          <div key={day.key} className="flex flex-col gap-3 border border-hairline bg-page-subtle p-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-[160px] flex-1 flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Day label</span>
                <input
                  type="text"
                  value={day.label}
                  onChange={(event) => mutateDay(day.key, { label: event.target.value })}
                  className="h-10 border border-hairline bg-surface px-2 text-[14px] text-ink"
                />
              </label>
              <label className="flex min-w-[140px] flex-1 flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Focus (optional)</span>
                <input
                  type="text"
                  value={day.focus}
                  placeholder="lower · press"
                  onChange={(event) => mutateDay(day.key, { focus: event.target.value })}
                  className="h-10 border border-hairline bg-surface px-2 text-[14px] text-ink"
                />
              </label>
              <button
                type="button"
                onClick={() => setDays((prev) => prev.filter((candidate) => candidate.key !== day.key))}
                className="inline-flex h-10 items-center gap-1 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:border-danger hover:text-danger"
              >
                <TrashIcon className="text-[12px]" /> Remove day
              </button>
            </div>

            <ul className="flex flex-col gap-2">
              {day.exercises.map((exercise) => (
                <li key={exercise.key} className="flex flex-col gap-2 border border-hairline bg-surface p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 text-[13px] font-bold text-ink">{exercise.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${exercise.name}`}
                      onClick={() =>
                        mutateDay(day.key, { exercises: day.exercises.filter((candidate) => candidate.key !== exercise.key) })
                      }
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center text-ink-muted hover:text-danger"
                    >
                      <TrashIcon className="text-[13px]" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                    <NumberField
                      label="Sets"
                      info={FIELD_HELP.sets}
                      value={String(exercise.sets)}
                      onChange={(value) => mutateExercise(day.key, exercise.key, { sets: Number.parseFloat(value) || 0 })}
                      min={1}
                      max={20}
                      testId={`builder-${exercise.key}-sets`}
                    />
                    <NumberField
                      label="Rep min"
                      info={FIELD_HELP.repMin}
                      value={String(exercise.repMin)}
                      onChange={(value) => mutateExercise(day.key, exercise.key, { repMin: Number.parseFloat(value) || 0 })}
                      min={1}
                      max={100}
                      testId={`builder-${exercise.key}-rep-min`}
                    />
                    <NumberField
                      label="Rep max"
                      info={FIELD_HELP.repMax}
                      value={String(exercise.repMax)}
                      onChange={(value) => mutateExercise(day.key, exercise.key, { repMax: Number.parseFloat(value) || 0 })}
                      min={1}
                      max={100}
                      testId={`builder-${exercise.key}-rep-max`}
                    />
                    <NumberField
                      label="Target RPE"
                      info={FIELD_HELP.targetRpe}
                      value={String(exercise.targetRpe)}
                      onChange={(value) => mutateExercise(day.key, exercise.key, { targetRpe: Number.parseFloat(value) || 0 })}
                      min={0}
                      max={10}
                      step={0.5}
                      testId={`builder-${exercise.key}-rpe`}
                    />
                    <Field
                      label={`Weight (${unit === 'imperial' ? 'lb' : 'kg'})`}
                      info={FIELD_HELP.targetWeight}
                      testId={`builder-${exercise.key}-weight`}
                    >
                      <input
                        type="number"
                        inputMode="decimal"
                        aria-label={`Weight (${unit === 'imperial' ? 'lb' : 'kg'})`}
                        value={exercise.targetWeightKg}
                        onChange={(event) => mutateExercise(day.key, exercise.key, { targetWeightKg: event.target.value })}
                        className="l2f-tabular h-10 w-full border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
                      />
                    </Field>
                    <NumberField
                      label="Rest (s)"
                      info={FIELD_HELP.rest}
                      value={String(exercise.restSeconds)}
                      onChange={(value) => mutateExercise(day.key, exercise.key, { restSeconds: Number.parseFloat(value) || 0 })}
                      min={0}
                      max={900}
                      step={15}
                      testId={`builder-${exercise.key}-rest`}
                    />
                    <TempoField
                      value={exercise.tempo}
                      onChange={(tempo) => mutateExercise(day.key, exercise.key, { tempo })}
                      testId={`builder-${exercise.key}-tempo`}
                    />
                    <GroupSelect
                      value={exercise.group}
                      onChange={(group) => mutateExercise(day.key, exercise.key, { group })}
                      testId={`builder-${exercise.key}-group`}
                    />
                    <NotesField
                      value={exercise.notes}
                      onChange={(notes) => mutateExercise(day.key, exercise.key, { notes })}
                      testId={`builder-${exercise.key}-notes`}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => setPickerDay(day.key)}
              className="inline-flex h-10 w-fit items-center gap-2 border border-dashed border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              <PlusIcon className="text-[12px]" /> Add movement to {day.label}
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setDays((prev) => [...prev, emptyDay(prev.length + 1)])}
          className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          <PlusIcon className="text-[13px]" /> Add day
        </button>
        <button
          type="button"
          disabled={busy}
          data-testid="builder-create"
          onClick={submit}
          className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          <CheckIcon className="text-[13px]" /> Create programme
        </button>
        <span className="text-[12px] text-ink-muted">
          {pluralize(days.length, 'day')} · {days.reduce((sum, day) => sum + day.exercises.length, 0)} movements
        </span>
      </div>

      {validation ? <LiveMessage message={validation} tone="danger" /> : null}

      {pickerDay ? (
        <MovementPicker
          title="Add a movement to this day"
          onClose={() => setPickerDay(null)}
          onPick={(option) => {
            setDays((prev) =>
              prev.map((day) =>
                day.key === pickerDay
                  ? {
                      ...day,
                      exercises: [
                        ...day.exercises,
                        {
                          key: key('ex'),
                          exerciseId: option.id,
                          name: option.name,
                          sets: 3,
                          repMin: 8,
                          repMax: 12,
                          targetRpe: 7,
                          targetWeightKg: '',
                          restSeconds: 90,
                          tempo: '',
                          notes: '',
                          group: { group: null, groupType: null },
                        },
                      ],
                    }
                  : day,
              ),
            );
            setPickerDay(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   The screen
   ------------------------------------------------------------------------ */

export function ProgrammeScreen() {
  const auth = useSession();
  const { unit } = useUnitPreference();

  const [overview, setOverview] = useState<ProgrammeOverview | null>(null);
  const [history, setHistory] = useState<PlanHistoryEntry[]>([]);
  const [source, setSource] = useState<'api' | 'demo' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<ChipTone>('info');

  const [builderOpen, setBuilderOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * New Block: one panel for name → start-from → when. When "empty" is chosen,
   * the panel hands off to the existing builder with the name and timing kept.
   */
  const [newBlockOpen, setNewBlockOpen] = useState(false);
  const [newBlockError, setNewBlockError] = useState<string | null>(null);
  const [pendingBlock, setPendingBlock] = useState<{
    name: string;
    when: 'now' | 'date' | 'later';
    startOn: string | null;
  } | null>(null);
  /**
   * When set, the screen shows that block instead of the active one. A lifter
   * with a coach-owned block AND their own active block needs this: the coach's
   * block is the one the grid starts from, but the lifter must still be able to
   * open and edit their own.
   */
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [editingExercise, setEditingExercise] = useState<string | null>(null);
  const [pickerDay, setPickerDay] = useState<string | null>(null);
  const [addingDay, setAddingDay] = useState(false);
  const [newDay, setNewDay] = useState({ label: '', focus: '' });
  const [pendingDayId, setPendingDayId] = useState<string | null>(null);
  const [selectedExercises, setSelectedExercises] = useState<Set<string>>(new Set());
  const [composer, setComposer] = useState<{ dayId: string; dayLabel: string } | null>(null);
  const [composerMovement, setComposerMovement] = useState<ExerciseOption | null>(null);
  const [previousSets, setPreviousSets] = useState<Map<string, string[]>>(new Map());

  /**
   * Instant Paint: the programme is the most-read, least-changed document in the
   * app — a coach writes it once a month — which makes it the best candidate for
   * painting from cache. Keyed by the SELECTED plan, because a lifter can hold a
   * coach-owned block and their own at the same time; keying by "active" alone
   * would show one while the other is open.
   */
  const programmeKey = `programme:${selectedPlanId ?? 'active'}`;

  useEffect(() => {
    let alive = true;
    const cached = isFresh(peek(programmeKey)) ? (peek(programmeKey) as { value?: { overview?: ProgrammeOverview; history?: PlanHistoryEntry[] } } | null) : null;
    if (cached?.value?.overview) {
      // Paint what we already have; the fetch below still runs and replaces it.
      setOverview(cached.value.overview);
      setHistory(cached.value.history ?? []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    Promise.all([api.programmeOverview(selectedPlanId), api.programmePlans()])
      .then(([programmeResult, historyResult]) => {
        if (!alive) return;
        setOverview(programmeResult.data);
        setSource(programmeResult.source);
        if (programmeResult.data) {
          write(programmeKey, {
            overview: programmeResult.data,
            history: historyResult.source === 'api' || auth.isDemo ? historyResult.data : [],
          });
        }
        // The history list has no written endpoint; a real account is never
        // shown the built-in demo history as if it were its own.
        setHistory(historyResult.source === 'api' || auth.isDemo ? historyResult.data : []);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setError(cause instanceof Error ? cause.message : 'Could not read your programme.');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce, selectedPlanId, programmeKey]);

  const reloadHistory = async () => {
    const result = await api.programmePlans();
    setHistory(result.source === 'api' || auth.isDemo ? result.data : []);
  };

  /**
   * New Block — the copy path writes name + structure + timing in one call and
   * opens the new block; the empty path hands the name and timing to the
   * builder, which writes the days in the same POST as before.
   */
  const createBlock = async (choice: NewBlockChoice) => {
    setNewBlockError(null);
    if (choice.mode === 'empty') {
      setPendingBlock({ name: choice.name, when: choice.when, startOn: choice.startOn });
      setNewBlockOpen(false);
      setBuilderOpen(true);
      setMessage(null);
      return;
    }
    setBusy(true);
    setMessage(null);
    // "Start on today" is start-now; a future date is saved with that first day
    // and the Programme banner offers Activate. Nothing starts on a read.
    const startNow =
      choice.when === 'now' ||
      (choice.when === 'date' && choice.startOn !== null && choice.startOn <= utcToday());
    const result = await api.copyProgrammePlan(
      choice.copyFromPlanId ?? '',
      choice.name,
      undefined,
      {
        startNow,
        startOn: choice.when === 'date' && !startNow ? choice.startOn : null,
      },
    );
    setBusy(false);
    if (result.problem || !result.data) {
      setNewBlockError(result.problem?.detail ?? 'The block could not be created.');
      return;
    }
    setNewBlockOpen(false);
    setSelectedPlanId(result.data.planId);
    setNonce((value) => value + 1);
    setTone('success');
    setMessage(
      startNow
        ? 'New block created and running. It replaced your previous block; the old one is in All Programmes.'
        : choice.when === 'date'
          ? `Scheduled for ${choice.startOn}. It is saved, not running — the Programme page offers Activate when you are ready.`
          : 'Saved as a draft. It sits in All Programmes until you activate it.',
    );
    void reloadHistory();
  };

  const apply = (result: ApiResult<ProgrammeOverview>, ok: string) => {
    // A programme write invalidates the cached copy for THIS plan only.
    if (result.data) invalidate(programmeKey);
    if (result.data) setOverview(result.data);
    setSource(result.source);
    if (result.source === 'demo' && !auth.isDemo) {
      setTone('warning');
      setMessage(
        'That change could not reach the API, so this screen is now showing the built-in demo programme — not your account. Nothing was saved.',
      );
    } else {
      setTone('success');
      setMessage(ok);
    }
  };

  const mutate = async (run: () => Promise<ApiResult<ProgrammeOverview>>, ok: string) => {
    setBusy(true);
    setMessage(null);
    const result = await run();
    setBusy(false);
    apply(result, ok);
  };

  const plan = overview?.plan ?? null;
  const days = useMemo(() => (overview ? buildDays(overview) : []), [overview]);
  // A block authored by a coach for the client belongs to the coach: the lifter
  // may view, start and log it, but every editing affordance is withheld (0019).
  const coachOwned = plan?.coachOwned === true;
  const canEdit = !coachOwned;

  /**
   * The last completed session's working sets, per exercise — the "previous
   * session" column the landing page shows. Read from the real log, one session
   * detail for the most recent completed session; never fabricated.
   */
  useEffect(() => {
    const planId = plan?.planId ?? null;
    if (!planId) {
      setPreviousSets(new Map());
      return undefined;
    }
    let alive = true;
    void (async () => {
      const history = await api.sessionHistory(12);
      const latest = history.data.find((entry) => entry.status === 'completed');
      if (!latest) {
        if (alive) setPreviousSets(new Map());
        return;
      }
      const detail = await api.sessionDetail(latest.sessionId);
      if (!alive) return;
      const map = new Map<string, string[]>();
      for (const exercise of detail.data) {
        const sets = exercise.loggedSets
          .filter((set) => !set.isWarmup && set.status !== 'pending')
          .sort((a, b) => a.setIndex - b.setIndex)
          .slice(0, 3)
          .map((set) => formatLoggedSet(set, unit));
        if (sets.length) map.set(exercise.exerciseId, sets);
      }
      setPreviousSets(map);
    })();
    return () => {
      alive = false;
    };
  }, [plan?.planId, unit]);

  const createProgramme = async (draft: ProgrammeDraft) => {
    const when = pendingBlock?.when ?? 'now';
    const startOn = pendingBlock?.startOn ?? null;
    // "Start on today" is start-now; a future date is saved inactive and the
    // Programme banner offers Activate. Nothing starts on a read.
    const startNow = when === 'now' || (when === 'date' && startOn !== null && startOn <= utcToday());
    setBusy(true);
    setMessage(null);
    const result = await api.createProgramme({
      ...draft,
      track: draft.track ?? auth.user?.track ?? 'general',
      // The When choice lives in New Block, not in a second checkbox here.
      activate: startNow,
      ...(when === 'date' && startOn ? { startsOn: startOn } : {}),
    });
    setBusy(false);
    if (!result.data) {
      setTone('danger');
      setMessage('The programme could not be created.');
      return;
    }
    apply(result, `“${draft.name}” is ready — ${pluralize(draft.days.length, 'day')}.`);
    setBuilderOpen(false);
    // A drafted or scheduled block must open so it can be edited before it runs;
    // "start now" leaves the screen on the active block.
    setSelectedPlanId(startNow ? null : (result.data.plan?.planId ?? null));
    setPendingBlock(null);
    void reloadHistory();
  };

  const startDay = async (dayId: string, label: string) => {
    // A real gesture opens the runtime's audio context before the countdown.
    unlockRuntimeAudio();
    setBusy(true);
    setMessage(null);
    const result = await api.startSession({ planDayId: dayId, label });
    setBusy(false);
    if (result.problem) {
      // The API answered and rejected the create: print ITS reason rather than
      // pretending the request never reached it.
      setTone('danger');
      setMessage(
        `The API could not start that session — ${result.problem.status} ${result.problem.title}: ${result.problem.detail ?? 'no detail'}`,
      );
      return;
    }
    if ((result.source === 'api' || auth.isDemo) && result.data?.session) {
      // The day's Start session opens the runtime panel over the Grid, not
      // just the grid — pressing it must land the lifter in the workout.
      requestRuntimeOpen('countdown');
      navigate('/grid');
      setTone('success');
      setMessage(`Started ${label}.`);
      return;
    }
    // The API never answered. It may still have created the session, so re-read
    // in place before telling the lifter nothing was saved — a lost response
    // (a LAN candidate that missed while the page settled) must not strand a
    // session that really exists.
    if (!auth.isDemo) {
      const recovered = await api.activeSession();
      const live = recovered.source === 'api' && Boolean(recovered.data?.session);
      if (live) {
        requestRuntimeOpen('countdown');
        navigate('/grid');
        setTone('warning');
        setMessage('The response was lost, but the session is live on the server — resuming it.');
        return;
      }
    }
    setTone('warning');
    setMessage('The API could not start that session, so nothing was saved.');
  };

  const moveDay = (day: ProgrammeDay, direction: -1 | 1) => {
    const order = days.map((candidate) => candidate.dayId);
    const index = order.indexOf(day.dayId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    void mutate(() => api.reorderProgramme(plan?.planId ?? '', { dayIds: next }), `Moved ${day.label}.`);
  };

  const moveExercise = (day: ProgrammeDay, row: ProgrammeRow, direction: -1 | 1) => {
    // Reorder inside the block: members of a group move among themselves, and
    // ungrouped rows move among the day's ungrouped rows. Groups survive it.
    const dayOrder = moveWithinCluster(day.rows, row.planExerciseId, direction);
    if (!dayOrder) return;
    // The reorder endpoint takes the plan's prescriptions as one complete list,
    // warm-up included.
    const warmupOrder = (overview?.warmup?.rows ?? []).map((entry) => entry.planExerciseId);
    const globalOrder = [
      ...warmupOrder,
      ...days.flatMap((candidate) =>
        candidate.dayId === day.dayId ? dayOrder : candidate.rows.map((entry) => entry.planExerciseId),
      ),
    ];
    void mutate(
      () => api.reorderProgramme(plan?.planId ?? '', { planExerciseIds: globalOrder }),
      `Reordered ${row.movement ?? row.exerciseName}.`,
    );
  };

  const toggleSelectedExercise = (planExerciseId: string) => {
    setSelectedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(planExerciseId)) next.delete(planExerciseId);
      else next.add(planExerciseId);
      return next;
    });
  };

  const selectedIdsInDay = (day: ProgrammeDay): string[] =>
    day.rows.filter((row) => selectedExercises.has(row.planExerciseId)).map((row) => row.planExerciseId);

  const groupSelection = (day: ProgrammeDay, groupType: ExerciseGroupType) => {
    if (!plan) return;
    const ids = selectedIdsInDay(day);
    if (ids.length < 2) {
      setTone('danger');
      setMessage('Select at least two exercises to group them — a group of one is not a group.');
      return;
    }
    const group = nextGroupNumber(day.rows);
    if (group === null) {
      setTone('danger');
      setMessage('This day already uses all twelve groups.');
      return;
    }
    setSelectedExercises(new Set());
    void mutate(
      () => api.groupProgrammeExercises(plan.planId, day.dayId, { planExerciseIds: ids, group, groupType }),
      `${groupType === 'drop_set' ? 'Drop set' : 'Superset'} ${groupLetter(group)} set on ${ids.length} exercises.`,
    );
  };

  const ungroupSelection = (day: ProgrammeDay) => {
    if (!plan) return;
    const ids = selectedIdsInDay(day);
    if (!ids.length) return;
    setSelectedExercises(new Set());
    void mutate(
      () => api.groupProgrammeExercises(plan.planId, day.dayId, { planExerciseIds: ids, group: null }),
      'Group removed.',
    );
  };

  const addDay = async () => {
    if (!plan) return;
    const label = newDay.label.trim() || `Day ${days.length + 1}`;
    setBusy(true);
    setMessage(null);
    const result = await api.addProgrammeDay(plan.planId, {
      label,
      focus: newDay.focus.trim() ? [newDay.focus.trim()] : [],
      index: days.length + 1,
    });
    setBusy(false);
    if (result.data) {
      const next = result.data.overview;
      const dayId = result.data.dayId;
      const focus = newDay.focus.trim();
      // An API build that predates the `days` envelope returns only rows, so a
      // brand-new empty day would vanish. Keep it in local state until its first
      // movement (and, later, the server's `days`) arrives.
      setOverview(
        dayId && !(next.days ?? []).some((candidate) => candidate.id === dayId)
          ? {
              ...next,
              days: [
                ...(next.days ?? []),
                { id: dayId, dayIndex: days.length + 1, label, focus: focus ? [focus] : [], exerciseCount: 0 },
              ],
            }
          : next,
      );
    }
    setSource(result.source);
    setAddingDay(false);
    const dayId = result.data?.dayId ?? null;
    if (dayId) {
      setPendingDayId(dayId);
      setComposerMovement(null);
      setComposer({ dayId, dayLabel: label });
      setTone('success');
      setMessage(`${label} added — pick its first movement.`);
    } else if (result.source === 'demo' && !auth.isDemo) {
      setTone('warning');
      setMessage('That change could not reach the API, so nothing was saved.');
    } else {
      setTone('warning');
      setMessage(
        `${label} was created, but this API build returns only prescribed rows, so the new day's first movement could not be attached.`,
      );
    }
  };

  const saveComposer = (dayId: string, input: ProgrammeExerciseInput, name: string) => {
    if (!plan) return;
    setComposer(null);
    setComposerMovement(null);
    setPendingDayId(null);
    void mutate(() => api.addProgrammeExercises(plan.planId, dayId, input), `${name} added to the day.`);
  };

  const activate = async (entry: PlanHistoryEntry) => {
    await mutate(() => api.activateProgramme(entry.planId), `${entry.name} is now your active programme.`);
    // Activating is a statement about the running block: show that one, not
    // whichever past block happened to be open.
    setSelectedPlanId(null);
    void reloadHistory();
  };

  /**
   * Stop the running block without replacing it — "nothing runs for now". The
   * block stays in All Programmes, so the page's "no active block" state can
   * always be undone.
   */
  const deactivateBlock = async (entry: PlanHistoryEntry) => {
    await mutate(
      () => api.deactivateProgramme(entry.planId),
      `${entry.name} is no longer running. Nothing runs until you start a block.`,
    );
    setSelectedPlanId(null);
    void reloadHistory();
  };

  /** Delete an inactive block. The API refuses the active one; deactivate first. */
  const deleteBlock = async (entry: PlanHistoryEntry) => {
    setBusy(true);
    setMessage(null);
    const result = await api.deleteProgrammePlan(entry.planId);
    setBusy(false);
    if (result.problem) {
      setTone('danger');
      setMessage(result.problem.detail ?? 'That block could not be deleted.');
      return;
    }
    setTone('success');
    setMessage(`${entry.name} deleted.`);
    if (selectedPlanId === entry.planId) setSelectedPlanId(null);
    void reloadHistory();
  };

  const savePlanSettings = (patch: { name?: string; weeks?: number; deloadWeek?: number | null; isActive?: boolean }) => {
    if (!plan) return;
    void mutate(() => api.patchProgrammePlan(plan.planId, patch), 'Block settings saved.');
    setSettingsOpen(false);
    void reloadHistory();
  };

  const saveExercise = (row: ProgrammeRow, patch: ProgrammeExercisePatch) => {
    if (!plan) return;
    setEditingExercise(null);
    void mutate(
      () => api.patchProgrammeExercise(plan.planId, row.planExerciseId, patch),
      `${row.movement ?? row.exerciseName} updated.`,
    );
  };

  const removeExercise = (row: ProgrammeRow) => {
    if (!plan) return;
    void mutate(
      () => api.deleteProgrammeExercise(plan.planId, row.planExerciseId),
      `${row.movement ?? row.exerciseName} removed.`,
    );
  };

  const removeDay = (day: ProgrammeDay) => {
    if (!plan) return;
    void mutate(() => api.deleteProgrammeDay(plan.planId, day.dayId), `${day.label} removed.`);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading your programme" rows={4} />
        <LoadingPanel label="Loading your block" rows={3} />
      </div>
    );
  }

  if (error || !overview) {
    return (
      <EmptyState
        title="We could not read your programme"
        body={error ?? 'The API did not answer and there is no demo plan for this account.'}
        icon={<BarbellIcon className="h-8 w-8 text-accent" />}
        action={
          <Link
            to="/dashboard"
            className="inline-flex h-11 items-center border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Back to the dashboard
          </Link>
        }
      />
    );
  }

  const allProgrammes = (
    <AllProgrammesPanel
      audience="lifter"
      blocks={history}
      disabled={busy}
      // The inline list is the short one: name, dates, cadence and the actions.
      // The per-block numbers (sessions, days, sets, lineage) live on /programme/all.
      lean
      allHref="/programme/all"
      onView={(entry) => {
        setSelectedPlanId(entry.planId);
        setNewBlockOpen(false);
        setBuilderOpen(false);
      }}
      onActivate={(entry) => void activate(entry)}
      onDeactivate={(entry) => void deactivateBlock(entry)}
      onDelete={(entry) => void deleteBlock(entry)}
    />
  );

  /* ------------------------------- no plan yet -------------------------- */

  if (!plan) {
    // Blocks can exist with NOTHING running: the lifter deactivated the active
    // one (or every block was archived). That is "No active block", not "No
    // programme yet" — the list below is how they start one again.
    const hasBlocks = history.length > 0;
    return (
      <div className="flex flex-col gap-6">
        <ScreenHeader
          eyebrow="Your training"
          title="My Programme"
          badge={
            <StatusChip tone="neutral" icon={<GridIcon />}>
              {hasBlocks ? 'No active block' : 'No plan yet'}
            </StatusChip>
          }
          lead="Build your own week — days, movements, sets and numbers. No AI, no spreadsheet; the Grid reads straight from it."
        />
        <SourceNotice source={source} isDemoSession={auth.isDemo} />
        <LiveMessage message={message} tone={tone} />
        <ScheduledBlockBanner
          blocks={history}
          audience="lifter"
          busy={busy}
          onActivate={(entry) => void activate(entry)}
        />
        <CoachBlockReadyBanner blocks={history} busy={busy} onActivate={(entry) => void activate(entry)} />

        {newBlockOpen ? (
          <NewBlockPanel
            audience="lifter"
            current={null}
            blocks={history}
            busy={busy}
            error={newBlockError}
            onCancel={() => {
              setNewBlockOpen(false);
              setNewBlockError(null);
            }}
            onCreate={(choice) => void createBlock(choice)}
          />
        ) : builderOpen ? (
          <ProgrammeBuilder
            unit={unit}
            defaultName={pendingBlock?.name}
            defaultDays={4}
            busy={busy}
            onCancel={() => {
              setBuilderOpen(false);
              setPendingBlock(null);
            }}
            onCreate={(draft) => void createProgramme(draft)}
          />
        ) : (
          <div data-testid="programme-empty">
            <EmptyState
              title={hasBlocks ? 'No active block' : 'No programme yet'}
              body={
                hasBlocks
                  ? 'Nothing is running. Pick a block from All Programmes below to make it the running programme, or start a new one.'
                  : 'Start a block below, or generate a plan. Whichever you choose, the Grid starts from the prescription.'
              }
              icon={<SparkIcon className="h-8 w-8 text-accent" />}
              action={
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="new-block"
                    onClick={() => {
                      setNewBlockOpen(true);
                      setBuilderOpen(false);
                      setNewBlockError(null);
                    }}
                    className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
                  >
                    <PlusIcon className="text-[13px]" /> New Block
                  </button>
                  <Link
                    to="/plan/new"
                    className="inline-flex h-11 items-center gap-2 border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                  >
                    Generate a plan instead
                    <ArrowRightIcon />
                  </Link>
                </div>
              }
            />
          </div>
        )}

        {allProgrammes}
      </div>
    );
  }

  /* ------------------------------- the plan ----------------------------- */

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <ScreenHeader
        eyebrow="Your training"
        title="My Programme"
        badge={
          <span className="flex flex-wrap items-center gap-2">
            <StatusChip tone="accent" icon={<GridIcon />}>{plan.name}</StatusChip>
            {plan.isActive ? <StatusChip tone="success" icon={<CheckIcon />}>Active</StatusChip> : <StatusChip tone="warning">Inactive</StatusChip>}
          </span>
        }
        lead={`${auth.user?.displayName ?? 'You'} · ${pluralize(days.length, 'day')}/week · ${pluralize(plan.weeks, 'week')} block. ${
          coachOwned ? 'Your coach wrote this block and manages it.' : 'Training you wrote yourself.'
        }`}
        actions={
          // Only actions the lifter can TAKE live beside the title. New Block is
          // always available — even on a coach's block, a lifter may start their
          // own; editing is withheld because the block is the coach's (0019).
          <>
            <button
              type="button"
              data-testid="new-block"
              onClick={() => {
                setNewBlockOpen((value) => !value);
                setBuilderOpen(false);
                setSettingsOpen(false);
                setNewBlockError(null);
              }}
              className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
            >
              <PlusIcon className="text-[13px]" /> New Block
            </button>
            {coachOwned ? null : (
              <button
                type="button"
                data-testid="edit-block"
                onClick={() => setSettingsOpen((value) => !value)}
                className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
              >
                Edit block
              </button>
            )}
          </>
        }
      />

      <SourceNotice source={source} isDemoSession={auth.isDemo} />
      <LiveMessage message={message} tone={tone} />
      <ScheduledBlockBanner
        blocks={history}
        audience="lifter"
        busy={busy}
        onActivate={(entry) => void activate(entry)}
      />
      <CoachBlockReadyBanner blocks={history} busy={busy} onActivate={(entry) => void activate(entry)} />

      {coachOwned ? (
        <p
          data-testid="coach-owned-note"
          className="border border-hairline border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[12px] text-ink"
        >
          <span className="font-bold">This is your coach's programme.</span> Your coach wrote the days, movements and weeks
          and manages them; you can view it, start a session from it and log every set. If you want a block of your own,
          start one with <span className="font-bold">New Block</span> — activating it replaces this one in the active slot,
          and this block stays in All Programmes.
        </p>
      ) : null}

      {selectedPlanId ? (
        <p
          data-testid="viewing-block"
          className="flex flex-wrap items-center gap-2 border border-hairline border-l-2 border-l-warning bg-page-subtle px-3 py-2 text-[12px] text-ink"
        >
          <span>
            Viewing <span className="font-bold">{plan.name}</span> — not necessarily the block the Grid starts from.
          </span>
          <button
            type="button"
            data-testid="viewing-back"
            onClick={() => setSelectedPlanId(null)}
            className="inline-flex h-8 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Back to the active block
          </button>
        </p>
      ) : null}

      {/* New Block is available on ANY block, including a coach's: a lifter may
          always start their own programme. Gating the panel on `!coachOwned` was
          the bug that made the button look dead — the copy above even told the
          lifter to use it. */}
      {newBlockOpen ? (
        <NewBlockPanel
          audience="lifter"
          current={{ planId: plan.planId, name: plan.name }}
          blocks={history}
          busy={busy}
          error={newBlockError}
          onCancel={() => {
            setNewBlockOpen(false);
            setNewBlockError(null);
          }}
          onCreate={(choice) => void createBlock(choice)}
        />
      ) : null}

      {coachOwned ? null : builderOpen ? (
        <ProgrammeBuilder
          unit={unit}
          defaultName={pendingBlock?.name}
          defaultDays={Math.max(4, days.length)}
          busy={busy}
          onCancel={() => {
            setBuilderOpen(false);
            setPendingBlock(null);
          }}
          onCreate={(draft) => void createProgramme(draft)}
        />
      ) : null}

      {coachOwned ? null : settingsOpen ? (
        <Panel title="Block settings" subtitle="Rename the block, change its length or move the deload week.">
          <BlockSettings plan={plan} busy={busy} onSave={savePlanSettings} />
        </Panel>
      ) : null}

      {/* ---------------- block header ---------------- */}
      <div
        data-testid="block-card"
        className="flex flex-wrap items-center justify-between gap-4 rounded-card border border-hairline bg-surface px-5 py-4 shadow-card"
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Block</span>
            <span className="font-display text-[16px] font-black uppercase tracking-[-0.01em] text-ink">{plan.name}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Lifter</span>
            <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">{auth.user?.displayName ?? 'You'}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Length</span>
            <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
              {pluralize(plan.weeks, 'week')}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Cadence</span>
            <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
              {pluralize(days.length, 'day')} / week
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Deload</span>
            <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
              {plan.deloadWeek === null ? 'None' : `Week ${plan.deloadWeek}`}
            </span>
          </div>
          {/* Ownership is a fact about the block, so it is stated here beside
              Length and Cadence rather than floating beside the page title. */}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Managed by</span>
            <span
              data-testid="block-managed-by"
              className={cn(
                'flex items-center gap-1.5 font-mono text-[13px] font-bold',
                coachOwned ? 'text-accent' : 'text-ink',
              )}
            >
              {coachOwned ? (
                <>
                  <LockIcon className="text-[12px]" /> Your coach
                </>
              ) : (
                'You'
              )}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 md:items-end">
          <div className="flex flex-wrap items-center gap-1.5" role="list" aria-label="Block weeks">
            {plan.weekPlan.length ? (
              plan.weekPlan.map((week) => (
                <span
                  key={week.week}
                  role="listitem"
                  title={week.isDeload ? `Week ${week.week} — deload` : `Week ${week.week}`}
                  className={cn(
                    'l2f-tabular flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.06em]',
                    week.isDeload ? 'border-accent bg-accent/10 text-accent' : 'border-hairline text-ink-muted',
                  )}
                >
                  <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', week.isDeload ? 'bg-accent' : 'bg-ink-muted/40')} />
                  W{week.week}
                  {week.isDeload ? ' deload' : ''}
                </span>
              ))
            ) : (
              <StatusChip tone="neutral">{pluralize(plan.weeks, 'week')} block</StatusChip>
            )}
          </div>
          {/* The programme is undated; "when" is the schedule layer. The reminder
              sits with the block it schedules, not in the page-title row. */}
          <Link
            to="/schedule"
            data-testid="programme-schedule-link"
            className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <TimerIcon className="text-[12px]" /> Session reminder
          </Link>
        </div>
      </div>

      {!plan.isActive ? (
        <p
          data-testid="inactive-note"
          className="border border-hairline border-l-2 border-l-warning bg-page-subtle px-3 py-2 text-[12px] text-ink-muted"
        >
          This programme is not active. The Grid starts from your active block; use “Activate” in the list below to switch
          this one on.
        </p>
      ) : null}

      {/* The AI-coach projection slot only makes sense on the lifter's own block;
          a human coach's block is not "a plan you built yourself". */}
      {canEdit ? <LockedCoachSlot planSource={plan.source} testId="coach-locked-programme" /> : null}

      {/* ---------------- the shared warm-up ---------------- */}
      <WarmupCard
        warmup={overview.warmup ?? null}
        planId={plan.planId}
        editable={canEdit}
        unit={unit}
        busy={busy}
        otherPlanExerciseIds={days.flatMap((day) => day.rows.map((row) => row.planExerciseId))}
        onApplied={apply}
        audience="lifter"
      />

      {/* ---------------- repeat-the-block explanation ---------------- */}
      <details className="group rounded-card border border-hairline bg-surface">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
          <span className="text-[13px] font-bold uppercase tracking-[0.04em] text-ink">Repeat the block</span>
          <ChevronDownIcon className="text-base text-ink-muted transition-transform duration-200 group-open:rotate-180" />
        </summary>
        <div className="flex flex-col gap-3 border-t border-hairline px-5 py-4 text-[13px] text-ink-muted">
          <p>
            This block repeats every week for <span className="l2f-tabular font-mono font-bold text-ink">{plan.weeks}</span>{' '}
            {pluralize(plan.weeks, 'week')}. The days, movements and prescriptions stay the same; you progress them with the
            double-progression rule printed on each exercise.
          </p>
          {plan.deloadWeek !== null ? (
            <p className="border-l-2 border-l-accent pl-3">
              Week <span className="l2f-tabular font-mono font-bold text-ink">{plan.deloadWeek}</span> is lighter:{' '}
              <span className="font-bold text-ink">sets × 0.6</span>, <span className="font-bold text-ink">load −10 %</span>,{' '}
              <span className="font-bold text-ink">stop at RPE 7</span>. That is the deload printed on the plan — the app
              shows the number, it does not change your prescriptions behind your back.
            </p>
          ) : (
            <p className="border-l-2 border-l-accent pl-3">
              No deload week is set on this block. Set one under Edit block and it is shown as lighter:{' '}
              <span className="font-bold text-ink">sets × 0.6</span>, <span className="font-bold text-ink">load −10 %</span>,{' '}
              <span className="font-bold text-ink">stop at RPE 7</span>.
            </p>
          )}
          <p className="text-[12px]">
            “Repeat” is a description of the block, not a button that rewrites your log. Adjust any prescription with Edit
            block whenever you want to change it.
          </p>
        </div>
      </details>

      {/* ---------------- the days ---------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-[20px] font-black uppercase tracking-[-0.01em] text-ink">Your week</h2>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit ? (
            <button
              type="button"
              data-testid="add-day"
              onClick={() => {
                setAddingDay((value) => !value);
                setNewDay({ label: `Day ${days.length + 1}`, focus: '' });
              }}
              className="inline-flex h-10 items-center gap-2 border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              <PlusIcon className="text-[12px]" /> Add day
            </button>
          ) : (
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
              Your coach sets the days
            </span>
          )}
        </div>
      </div>

      {canEdit && addingDay ? (
        <Panel title="Add a day" subtitle="Name the day, then choose its first movement.">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Day label</span>
              <input
                type="text"
                value={newDay.label}
                data-testid="new-day-label"
                onChange={(event) => setNewDay((prev) => ({ ...prev, label: event.target.value }))}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Focus (optional)</span>
              <input
                type="text"
                value={newDay.focus}
                onChange={(event) => setNewDay((prev) => ({ ...prev, focus: event.target.value }))}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              data-testid="add-day-confirm"
              onClick={() => void addDay()}
              className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
            >
              <PlusIcon className="text-[13px]" /> Add day
            </button>
            <button
              type="button"
              onClick={() => setAddingDay(false)}
              className="inline-flex h-11 items-center border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
            >
              Cancel
            </button>
          </div>
        </Panel>
      ) : null}

      <div className="grid gap-4">
        {days.map((day, dayIndex) => {
          const totalSets = day.rows.reduce((sum, row) => sum + row.prescribedSets, 0);
          const selectedInDay = day.rows.filter((row) => selectedExercises.has(row.planExerciseId));
          const blocks = buildBlocks(day.rows);
          return (
            <section
              key={day.dayId}
              className="overflow-hidden rounded-card border border-hairline bg-surface shadow-card"
              aria-label={day.label}
              data-testid={`programme-day-${day.dayIndex}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-surface-raised px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center bg-brand-charcoal text-brand-white">
                    <BarbellIcon className="text-base" />
                  </span>
                  <div className="flex flex-col leading-tight">
                    <span className="font-display text-[15px] font-black uppercase tracking-[-0.01em] text-ink">
                      {day.label}
                      {day.focus.length ? <span className="text-ink-muted"> · {day.focus.join(' · ')}</span> : null}
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                      {pluralize(day.rows.length, 'exercise')} · {totalSets} working sets
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {canEdit ? (
                    <>
                      <button
                        type="button"
                        onClick={() => moveDay(day, -1)}
                        disabled={busy || dayIndex === 0}
                        aria-label={`Move ${day.label} earlier`}
                        className="inline-flex h-9 items-center border border-hairline px-2 text-[11px] font-bold text-ink-muted hover:border-accent hover:text-accent disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveDay(day, 1)}
                        disabled={busy || dayIndex === days.length - 1}
                        aria-label={`Move ${day.label} later`}
                        className="inline-flex h-9 items-center border border-hairline px-2 text-[11px] font-bold text-ink-muted hover:border-accent hover:text-accent disabled:opacity-30"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        data-testid={`add-exercise-${day.dayIndex}`}
                        onClick={() => {
                          setPendingDayId(null);
                          setComposerMovement(null);
                          setComposer({ dayId: day.dayId, dayLabel: day.label });
                        }}
                        className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                      >
                        <PlusIcon className="text-[11px]" /> Exercise
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeDay(day)}
                        disabled={busy}
                        aria-label={`Remove ${day.label}`}
                        className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:border-danger hover:text-danger disabled:opacity-40"
                      >
                        <TrashIcon className="text-[11px]" /> Day
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    disabled={busy}
                    data-testid={`start-day-${day.dayIndex}`}
                    onClick={() => void startDay(day.dayId, day.label)}
                    className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                  >
                    <ArrowRightIcon className="text-[11px]" /> Start session
                  </button>
                </div>
              </div>

              {canEdit && selectedInDay.length ? (
                <div
                  data-testid={`group-actions-${day.dayIndex}`}
                  className="flex flex-wrap items-center gap-2 border-b border-hairline bg-page-subtle px-5 py-2.5"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink">
                    {selectedInDay.length} selected
                  </span>
                  <button
                    type="button"
                    data-testid="group-superset"
                    disabled={busy || selectedInDay.length < 2}
                    onClick={() => groupSelection(day, 'superset')}
                    className="inline-flex h-9 items-center border border-hairline bg-surface px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                  >
                    Group as superset
                  </button>
                  <button
                    type="button"
                    data-testid="group-drop-set"
                    disabled={busy || selectedInDay.length < 2}
                    onClick={() => groupSelection(day, 'drop_set')}
                    className="inline-flex h-9 items-center border border-hairline bg-surface px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                  >
                    Group as drop set
                  </button>
                  <button
                    type="button"
                    data-testid="ungroup"
                    disabled={busy}
                    onClick={() => ungroupSelection(day)}
                    className="inline-flex h-9 items-center border border-hairline bg-surface px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:border-accent hover:text-accent disabled:opacity-40"
                  >
                    Ungroup
                  </button>
                  <button
                    type="button"
                    data-testid="clear-selection"
                    onClick={() => setSelectedExercises(new Set())}
                    className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-ink"
                  >
                    Clear
                  </button>
                </div>
              ) : null}

              <ol className="divide-y divide-hairline">
                {blocks.map((block, blockIndex) => {
                  const isGroup = block.rows.length > 1;
                  return (
                    <li
                      key={`${day.dayId}-${block.group ?? 'solo'}-${blockIndex}`}
                      className="flex flex-col"
                    >
                      {isGroup ? (
                        <div
                          data-testid={`group-tag-${day.dayIndex}-${block.group}`}
                          className="flex items-center gap-2 border-b border-hairline bg-page-subtle px-5 py-2"
                        >
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-black text-on-accent">
                            {groupLetter(block.group)}
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-accent">
                            {groupTagText(block.group, block.groupType)} —{' '}
                            {block.groupType === 'drop_set' ? 'same movement, descending load' : 'performed back to back'}
                          </span>
                        </div>
                      ) : null}

                      {block.rows.map((row) => {
                        const line = toLine(row);
                        const memberIndex = block.rows.findIndex((candidate) => candidate.planExerciseId === row.planExerciseId);
                        const checked = selectedExercises.has(row.planExerciseId);
                        const previous = previousSets.get(row.exerciseId);
                        return (
                          <div
                            key={row.planExerciseId}
                            data-testid={`programme-row-${row.planExerciseId}`}
                            className={cn('flex flex-col gap-3 px-5 py-4', checked && 'bg-accent/[0.04]')}
                          >
                            <div className="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-cols-[minmax(0,2.1fr)_minmax(0,1.5fr)_minmax(0,1fr)]">
                              <div className="flex flex-col gap-1.5">
                                <span className="flex items-start gap-2">
                                  {canEdit ? (
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      data-testid={`select-exercise-${row.planExerciseId}`}
                                      aria-label={`Select ${row.movement ?? row.exerciseName} for grouping`}
                                      onChange={() => toggleSelectedExercise(row.planExerciseId)}
                                      className="mt-[3px] h-4 w-4 shrink-0 border border-hairline accent-[color:var(--l2f-accent)]"
                                    />
                                  ) : null}
                                  <span className="text-[14px] font-bold leading-tight text-ink">
                                    {row.movement ?? row.exerciseName}
                                    {row.variant ? <span className="font-semibold text-ink-muted">, {row.variant}</span> : null}
                                  </span>
                                </span>
                                <span className="flex flex-wrap items-center gap-1.5">
                                  {row.muscleGroup ? <StatusChip tone="neutral">{row.muscleGroup}</StatusChip> : null}
                                  {row.equipment ? <StatusChip tone="neutral">{row.equipment}</StatusChip> : null}
                                  {row.perSide ? <StatusChip tone="accent">per side</StatusChip> : null}
                                  {row.isBodyweight ? <StatusChip tone="success">bodyweight</StatusChip> : null}
                                  {row.supersetGroup !== null ? (
                                    <StatusChip tone="info">{groupTagText(row.supersetGroup, row.groupType)}</StatusChip>
                                  ) : null}
                                </span>
                                <CollapsedNote id={`programme-${row.planExerciseId}`} note={row.notes} />
                              </div>

                              <div className="flex flex-col gap-2">
                                <div className="flex items-baseline gap-2">
                                  <span className="l2f-tabular font-mono text-[15px] font-bold text-ink">
                                    {row.prescribedSets} × {formatReps(line)}
                                  </span>
                                  {row.tempo ? (
                                    <span className="rounded-sm border border-hairline px-1.5 py-[1px] font-mono text-[10px] font-bold text-ink-muted">
                                      tempo {row.tempo}
                                    </span>
                                  ) : null}
                                </div>
                                <span className="text-[11px] text-ink-muted">{formatPrescription(line, unit)}</span>
                                <PreviousSetLine sets={previous ?? []} />
                              </div>

                              <div className="flex flex-col gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Prescribed</span>
                                <span className="flex items-center gap-1 text-[12px] font-bold text-accent">
                                  <TimerIcon className="text-[12px]" />
                                  {row.restSeconds ? `${row.restSeconds}s rest` : 'rest as needed'}
                                </span>
                                {row.targetWeightKg !== null ? (
                                  <span className="text-[11px] text-ink-muted">
                                    {formatWeight(row.targetWeightKg, unit)} target load
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            {canEdit && editingExercise === row.planExerciseId ? (
                              <ExerciseEditor
                                row={row}
                                unit={unit}
                                busy={busy}
                                onSave={(patch) => saveExercise(row, patch)}
                                onCancel={() => setEditingExercise(null)}
                              />
                            ) : canEdit ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  data-testid="exercise-edit"
                                  onClick={() => setEditingExercise(row.planExerciseId)}
                                  className="inline-flex h-9 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveExercise(day, row, -1)}
                                  disabled={busy || memberIndex === 0}
                                  aria-label={`Move ${row.movement ?? row.exerciseName} earlier`}
                                  className="inline-flex h-9 items-center border border-hairline px-2 text-[11px] font-bold text-ink-muted hover:border-accent hover:text-accent disabled:opacity-30"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveExercise(day, row, 1)}
                                  disabled={busy || memberIndex === block.rows.length - 1}
                                  aria-label={`Move ${row.movement ?? row.exerciseName} later`}
                                  className="inline-flex h-9 items-center border border-hairline px-2 text-[11px] font-bold text-ink-muted hover:border-accent hover:text-accent disabled:opacity-30"
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => void removeExercise(row)}
                                  aria-label={`Remove ${row.movement ?? row.exerciseName}`}
                                  className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:border-danger hover:text-danger disabled:opacity-40"
                                >
                                  <TrashIcon className="text-[11px]" /> Remove
                                </button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </li>
                  );
                })}
              </ol>

              {canEdit && composer?.dayId === day.dayId ? (
                <div className="border-t border-hairline p-4">
                  <ExerciseComposer
                    dayLabel={day.label}
                    movement={composerMovement}
                    unit={unit}
                    busy={busy}
                    onChooseMovement={() => setPickerDay(day.dayId)}
                    onSave={(input, name) => saveComposer(day.dayId, input, name)}
                    onCancel={() => {
                      setComposer(null);
                      setComposerMovement(null);
                      setPendingDayId(null);
                    }}
                  />
                </div>
              ) : null}
            </section>
          );
        })}
      </div>

      {/* ---------------- the RPE / RIR / pain legend ---------------- */}
      <details data-testid="rpe-legend" className="group rounded-card border border-hairline bg-surface">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
          <span className="text-[13px] font-bold uppercase tracking-[0.04em] text-ink">
            RPE · reps in reserve · % effort · pain scale
          </span>
          <ChevronDownIcon className="text-base text-ink-muted transition-transform duration-200 group-open:rotate-180" />
        </summary>
        <div className="overflow-x-auto border-t border-hairline">
          <table className="w-full min-w-[620px] border-separate border-spacing-0 text-left">
            <caption className="sr-only">The RPE, reps-in-reserve, effort and pain reference.</caption>
            <thead>
              <tr>
                {['RPE', 'Reps in reserve', '% effort', 'What it feels like', 'Pain', 'Pain description'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-hairline px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="l2f-tabular font-mono text-[12px]">
              {RPE_REFERENCE.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className="border-b border-hairline px-4 py-2 text-left font-bold text-ink">
                    {row.label}
                  </th>
                  <td className="border-b border-hairline px-4 py-2 text-ink-muted">{row.rir}</td>
                  <td className="border-b border-hairline px-4 py-2 text-ink-muted">{row.percentEffort}</td>
                  <td className="border-b border-hairline px-4 py-2 font-sans text-[12px] text-ink">{row.effort}</td>
                  <td className="border-b border-hairline px-4 py-2 font-bold text-ink">{row.painLevel}</td>
                  <td className="border-b border-hairline px-4 py-2 font-sans text-[12px] text-ink-muted">{row.pain}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {allProgrammes}

      {pickerDay ? (
        <MovementPicker
          title={pendingDayId ? 'Add the first movement' : 'Add a movement to this day'}
          onClose={() => setPickerDay(null)}
          onPick={(option) => {
            setComposerMovement(option);
            setPickerDay(null);
          }}
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Block settings form
   ------------------------------------------------------------------------ */

function BlockSettings({
  plan,
  busy,
  onSave,
}: {
  plan: NonNullable<ProgrammeOverview['plan']>;
  busy: boolean;
  onSave: (patch: { name?: string; weeks?: number; deloadWeek?: number | null; isActive?: boolean }) => void;
}) {
  const [name, setName] = useState(plan.name);
  const [weeks, setWeeks] = useState(plan.weeks);
  const [deload, setDeload] = useState<number | null>(plan.deloadWeek);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Block name</span>
          <input
            type="text"
            value={name}
            data-testid="settings-name"
            onChange={(event) => setName(event.target.value)}
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Weeks</span>
          <input
            type="number"
            min={1}
            max={52}
            value={weeks}
            data-testid="settings-weeks"
            onChange={(event) => setWeeks(Math.max(1, Math.min(52, Number.parseInt(event.target.value, 10) || 1)))}
            className="l2f-tabular h-11 border border-hairline bg-page px-3 font-mono text-[15px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Deload week</span>
          <select
            value={deload === null ? 'none' : String(deload)}
            onChange={(event) => setDeload(event.target.value === 'none' ? null : Number.parseInt(event.target.value, 10))}
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          >
            <option value="none">No deload</option>
            {Array.from({ length: weeks }, (_, index) => index + 1).map((week) => (
              <option key={week} value={week}>
                Week {week}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          data-testid="settings-save"
          onClick={() => onSave({ name: name.trim() || plan.name, weeks, deloadWeek: deload })}
          className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          <CheckIcon className="text-[13px]" /> Save block
        </button>
        <button
          type="button"
          onClick={() => onSave({ isActive: !plan.isActive })}
          className="inline-flex h-11 items-center border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          {plan.isActive ? 'Deactivate this block' : 'Activate this block'}
        </button>
      </div>
    </div>
  );
}
